import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import StructureSetItem from '~/components/Structures/StructureSetItem';
import useAvailableStructureSets, {
    AvailableStructureSet,
} from '~/hooks/structure-set/useAvailableStructureSets';
import useDeleteDataset from '~/hooks/useDeleteDataset';
import useStructureSetSelection from '~/hooks/structure-set/useStructureSetSelection';
import { currentStructureSetIdState } from '~/state/atoms/structureSet/structureSetAtoms';

export default function StructureSetList() {
    const intl = useIntl();

    const availableStructureSets = useAvailableStructureSets(true);
    const currentStructureSetId = useRecoilValue(currentStructureSetIdState);
    const { handleStructureSetChange, imageChangeConfirmation } =
        useStructureSetSelection({});
    const { deleteStructureSet } = useDeleteDataset();

    const [deleteTarget, setDeleteTarget] =
        useState<AvailableStructureSet | null>(null);

    const handleConfirmDelete = () => {
        if (deleteTarget?.structureSetObject) {
            deleteStructureSet(deleteTarget.structureSetObject);
        }
        setDeleteTarget(null);
    };

    if (availableStructureSets.length === 0) {
        return null;
    }

    return (
        <>
            <Box>
                <Box px={1.5} pt={1.5} pb={1}>
                    <Typography fontSize={12} color="primary" fontWeight={500}>
                        <FormattedMessage id="structureSets" />
                    </Typography>
                </Box>
                {availableStructureSets.map((ss) => {
                    const isActive =
                        currentStructureSetId === ss.sopInstanceUID;
                    return (
                        <Box
                            key={ss.sopInstanceUID}
                            borderTop="1px solid"
                            borderColor="divider"
                        >
                            <StructureSetItem
                                structureSet={ss}
                                isActive={isActive}
                                disabled={false}
                                onClick={() => handleStructureSetChange(ss)}
                                onDelete={
                                    ss.structureSetObject
                                        ? () => setDeleteTarget(ss)
                                        : undefined
                                }
                            />
                        </Box>
                    );
                })}
            </Box>

            <ConfirmationDialog
                open={imageChangeConfirmation.open}
                title={<FormattedMessage id="confirmImageChange.title" />}
                message={<FormattedMessage id="confirmImageChange.message" />}
                onConfirm={imageChangeConfirmation.onConfirm}
                onCancel={imageChangeConfirmation.onCancel}
                mode="primary"
            />

            <ConfirmationDialog
                open={deleteTarget !== null}
                title={intl.formatMessage({ id: 'deleteStructureSet' })}
                message={intl.formatMessage({
                    id: 'deleteStructureSetConfirm',
                })}
                onConfirm={handleConfirmDelete}
                onCancel={() => setDeleteTarget(null)}
                mode="error"
            />
        </>
    );
}
